import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { getItem } from 'utils/storageUtils'
import { checkIsEmailUnique, checkIsUsernameUnique } from 'utils/apiCalls'
import { toast } from 'components/Toast'
import {
  getAllCountriesStart,
  getRegistrationFieldsStart,
  getTenantDetailsStart,
  userSignUpStart
} from 'store/redux-slices/login'
import { sendStrictPhoneOTPStart } from 'store/redux-slices/user'
import { Buffer } from 'buffer'
import { formatDateMDY } from 'utils/dateFormatter'
import { browserFingerPrint } from 'utils/browserFingerprint'
import { getAffiliateCodeForPayload } from 'utils/signup'
import { closeModal } from 'store/redux-slices/modal'
import { MODALS } from 'constants/modals'
import { AUTH_MODAL_STEPS, PHONE_VERIFICATION_TYPE } from 'constants/general'

const useSignupModalFlow = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { languageCode } = useParams()
  const checkKycWithSignup = process.env.REACT_APP_KYC_WITH_SIGNUP === 'true'

  const { regFields, tenantDetails, allCountries, loading } = useSelector((state) => state.login)
  const { languages, languageData, selectedLanguageCode, userIPCountry, phoneVerificationType } = useSelector((state) => state.language)
  const generalConfig = useSelector((state) => state.uiConfig?.general)

  const [currentStep, setCurrentStep] = useState(AUTH_MODAL_STEPS.EMAIL_VERIFICATION) // 1 = email verification, 2 = complete form
  const [verifiedEmail, setVerifiedEmail] = useState('')
  const [language, setLanguage] = useState(
    languageCode || selectedLanguageCode?.toLowerCase() || getItem('language')?.toLowerCase()
  )
  const [disableSubmit, setDisableSubmit] = useState(false)
  const [search, setSearch] = useState('')

  useEffect(() => {
    dispatch(getRegistrationFieldsStart())
    dispatch(getTenantDetailsStart())
  }, [])

  useEffect(() => {
    dispatch(getAllCountriesStart())
  }, [selectedLanguageCode])

  // Step 1: Verify email uniqueness
  const handleEmailVerification = async ({ email }) => {
    setDisableSubmit(true)
    try {
      await checkIsEmailUnique({ email })
      setVerifiedEmail(email)
      setCurrentStep(AUTH_MODAL_STEPS.COMPLETE_FORM)
      toast(languageData?.emailVerified || 'Email verified! Please complete your registration.', 'success')
    } catch (error) {
      toast(languageData?.emailAlreadyRegistered || 'Email already registered', 'error')
    } finally {
      setDisableSubmit(false)
    }
  }

  // Step 2: Complete signup
  const handleCompleteSignup = async (formValues, acceptConditions) => {
    setDisableSubmit(true)

    // Check username uniqueness
    try {
      await checkIsUsernameUnique({ username: formValues.username })
    } catch (error) {
      toast(languageData?.userNameAlreadyTaken || 'Username already taken', 'error')
      setDisableSubmit(false)
      return { error: 'username', message: languageData?.userNameAlreadyTaken }
    }

    // Validate terms and conditions
    let toastMessage = `${languageData?.pleaseAccept}`

    if (!acceptConditions.termsAndConditions) {
      toastMessage += ` ${languageData?.acceptTerms}`
    }

    let modeOfCommShouldValidate = false

    if (regFields?.sms === 2 || regFields?.newsLetter === 2 || regFields?.newsletterAndSms === 2) {
      modeOfCommShouldValidate = true
    }

    if (
      modeOfCommShouldValidate &&
      (
        (regFields?.newsLetter === 2 && !acceptConditions?.newsLetter) ||
        (regFields?.sms === 2 && !acceptConditions?.sms) ||
        (regFields?.newsletterAndSms === 2 && !acceptConditions?.newsletterAndSms)
      )
    ) {
      if (acceptConditions.termsAndConditions) {
        toastMessage += `${languageData?.modeOfComm}`.replace('and', '')
      } else {
        toastMessage += ` ${languageData?.modeOfComm}`
      }
    } else {
      modeOfCommShouldValidate = false
    }

    if (!acceptConditions.termsAndConditions || modeOfCommShouldValidate) {
      toast(`${toastMessage} ${languageData?.beforeNextStep}`, 'error')
      setDisableSubmit(false)
      return { error: 'conditions' }
    }

    // Prepare signup data
    const initialState = prepareSignupData(formValues)

    // Submit signup
    const clearAuthModalQueryParam = () => {
      const params = new URLSearchParams(location.search)
      params.delete('auth-modal')
      navigate(
        {
          pathname: location.pathname,
          search: params.toString()
        },
        { replace: true }
      )
    }

    // Submit signup or send STRICT OTP
    if (phoneVerificationType === PHONE_VERIFICATION_TYPE.STRICT) {
      dispatch(
        sendStrictPhoneOTPStart({
          phone: initialState.phone,
          phoneCode: initialState.phoneCode,
          signupData: initialState,
          setDisableSubmit
        })
      )
    } else {
      dispatch(
        userSignUpStart({
          initialState,
          navigate,
          returnTo: window.locationPath,
          setDisableSubmit,
          checkKycWithSignup,
          kycFailedErrorMsg: languageData?.kycOnSignupFailed || 'Signup failed, please reach out to support',
          callBack: () => {
            clearAuthModalQueryParam()
            dispatch(closeModal(MODALS.AUTH))
          }
        })
      )
    }
  }

  const prepareSignupData = (formValues) => {
    const arr =
      regFields &&
      Object.keys(regFields)
        .map((keyName, i) => regFields[keyName] !== false && keyName)
        .filter((ele) => ele !== false && ele)

    const initialState =
      arr &&
      arr.reduce((accumulator, currentValue) => {
        accumulator[currentValue] = ''
        return accumulator
      }, {})

    for (const k in initialState) {
      if (k === 'newsLetter' || k === 'sms' || k === 'newsletterAndSms') {
        initialState[k] = formValues[k] ? 'true' : 'false'
      } else {
        initialState[k] = formValues[k]
      }
    }

    if (regFields.phone) {
      initialState.phoneCode = formValues.phoneCode
    }

    if (regFields.address) {
      initialState.city = formValues.city?.trim()
      initialState.zipCode = formValues.zipCode?.trim()
    }

    for (const k in initialState) {
      if (
        regFields[k] === 0 ||
        initialState[k] === '' ||
        initialState[k] === null
      ) {
        delete initialState[k]
        if (k === 'newsLetter') initialState.newsLetter = 'false'
        if (k === 'sms') initialState.sms = 'false'
      }
    }

    const encryptedPass = Buffer.from(initialState.password).toString('base64')
    initialState.password = encryptedPass
    initialState.confirmPassword && (initialState.confirmPassword = encryptedPass)
    initialState.dateOfBirth && (initialState.dateOfBirth = formatDateMDY(initialState.dateOfBirth))
    initialState.uniqueKey = browserFingerPrint()

    if (initialState.affiliateCode) {
      if (initialState.token) {
        delete initialState.token
      }
    } else {
      const refToken = getAffiliateCodeForPayload()
      if (refToken) {
        initialState.token = refToken
      }
      delete initialState.affiliateCode
    }

    if (formValues.recaptchaToken) {
      initialState.recaptchaToken = formValues.recaptchaToken
    }

    return initialState
  }

  const showStar = (fieldName) =>
    fieldName === 2 && <span className='text-danger'>&nbsp;*&nbsp;</span>

  const goBackToEmailStep = () => {
    setCurrentStep(AUTH_MODAL_STEPS.EMAIL_VERIFICATION)
  }

  return {
    currentStep,
    verifiedEmail,
    loading,
    regFields,
    tenantDetails,
    generalConfig,
    allCountries,
    languageData,
    language,
    selectedLanguageCode,
    languageCode,
    languages,
    setLanguage,
    disableSubmit,
    setDisableSubmit,
    handleEmailVerification,
    handleCompleteSignup,
    goBackToEmailStep,
    showStar,
    search,
    setSearch,
    countryCode: userIPCountry
  }
}

export default useSignupModalFlow
